import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { GetClaimFeature, type ApiGetClaimFeatureResponse } from '@/infrastructure/api-functions/core-search-api/get-claim-feature-api';
import { GetClaimCompareMatch } from '@/infrastructure/api-functions/core-search-api/get-claim-compare-match-api';
import { GetClaimCompareTranslation } from '@/infrastructure/api-functions/core-search-api/get-claim-compare-translation-api';
import _ from 'lodash';
import type { GetClaimFeatureRepository, GetClaimCompareMatchRepository, GetClaimCompareTranslationRepository, SetClaimCompareTranslationCacheRepository, GetClaimCompareExportRepository, GetClaimCompareResultRepository, GetComparePatentRepository } from '../../services/claim-charting-repository';
import type { ClaimFeatureData, GetClaimCompareMatchParams, ClaimCompareMatchData, GetClaimCompareTranslationParams, ClaimCompareTranslationData, GetClaimCompareExportParams, SetClaimCompareTranslationCacheParams, CompareFeatureData, FeatureCompareState, GetComparePatentData } from '../../types';
import { SetClaimCompareTranslationCache } from '@/infrastructure/api-functions/core-search-api/get-claim-compare-translation-cache-api';
import { GetClaimCompareExport } from '@/infrastructure/api-functions/core-search-api/get-claim-compare-export-api';
import { GetClaimCompareResult, type ApiGetClaimCompareResultRequest, type ApiGetClaimCompareResultResponse } from '@/infrastructure/api-functions/core-search-api/get-claim-compare-result-api';
import type { SSEResponse, SSEStreamService } from '@patsnap-utils/sse';
import { StreamStatus } from '../../types/enum';
import { SearchComparePatent } from '@/infrastructure/api-functions/core-search-api/core-search-api';
@injectable()
export class ClaimChartingRepositoryImpl implements
    GetClaimFeatureRepository,
    GetClaimCompareMatchRepository,
    GetClaimCompareTranslationRepository,
    GetClaimCompareExportRepository,
    SetClaimCompareTranslationCacheRepository,
    GetClaimCompareResultRepository,
    GetComparePatentRepository
{
    private _streamClaim: SSEStreamService<ApiGetClaimFeatureResponse> | null = null;
    private _streamCompare: SSEStreamService<ApiGetClaimCompareResultResponse> | null = null;
    @inject(GetClaimFeature)
    private _getClaimFeature!: GetClaimFeature;
    @inject(GetClaimCompareMatch)
    private _getClaimCompareMatch!: GetClaimCompareMatch;
    @inject(GetClaimCompareTranslation)
    private _getClaimCompareTranslation!: GetClaimCompareTranslation;
    @inject(GetClaimCompareExport)
    private _getClaimCompareExport!: GetClaimCompareExport;
    @inject(SetClaimCompareTranslationCache)
    private _setClaimCompareTranslationCache!: SetClaimCompareTranslationCache;
    @inject (GetClaimCompareResult)
    private _getClaimCompareResult!: GetClaimCompareResult;
    @inject (SearchComparePatent)
    private _searchComparePatent!: SearchComparePatent;
    constructor() {
    }

    getClaimFeatures(
        patentId: string,
        callback: (status: StreamStatus, content?: ClaimFeatureData) => void,
        errorCallback?: (error: { error_code?: number }) => void,
    ): SSEStreamService<ApiGetClaimFeatureResponse> {
        this._streamClaim = this._getClaimFeature.getClaimFeature({
            patent_id: patentId,
        })
            .onMessage((data: SSEResponse<ApiGetClaimFeatureResponse>) => {
                const { status, content } = data.data ?? {};
                if (content) {
                    const getData = () => {
                        const claims = content.separate_claims.map(item => {
                            return {
                                name: item.name,
                                isIndep: item.indep_claim, // indep_claim：true时是主权
                                features: item.separates.map(feature => ({ value: feature, id: _.uniqueId() })),
                            };
                        });
        
                        return {
                            claims,
                            patentSN: content.pn,
                            claimLang: content.claim_lang,
                        };
                    };
                    callback(status, getData());
                } else {
                    callback(status);
                }
                if (status === StreamStatus.FINISH) {
                    this._streamClaim?.close();
                }
            })
            .onError((error: { error_code?: number }) => {
                errorCallback?.(error);
            });

        this._streamClaim && this._streamClaim.start();
        return this._streamClaim;
    }

    async getClaimCompareMatch(data: GetClaimCompareMatchParams): Promise<ClaimCompareMatchData> {
        const res = await this._getClaimCompareMatch.getClaimCompareMatch({
            source_text: data.sourceText,
            source_patent: data.sourcePatent,
            source_lang: data.sourceLang,
            target_type: data.targetType,
        });
        return {
            matchText: res.match_text[0],
        };
    }

    getClaimCompareTranslation(data: GetClaimCompareTranslationParams): Promise<ClaimCompareTranslationData> {
        return this._getClaimCompareTranslation.getClaimCompareTranslation({
            patent_id: data.patentId,
            source_lang: data.sourceLang,
            trans_lang: data.transLang,
            text: data.text,
        });
    }

    getClaimCompareExport(data: GetClaimCompareExportParams): Promise<string> {
        return this._getClaimCompareExport.getClaimCompareExport({
            file_name: data.fileName,
            charts: data.charts.map(ele => ({
                sheet_name: ele.sheetName,
                head: ele.head,
                body: ele.body,
            })),
        });
    }

    setClaimCompareTranslationCache(data: SetClaimCompareTranslationCacheParams): Promise<ClaimCompareTranslationData>{
        return this._setClaimCompareTranslationCache.setClaimCompareTranslationCache({
            patent_id: data.patentId,
            translation: data.translation,
            trans_lang: data.transLang,
            text: data.text,
        });
    }

    _createClaimCompareParams(params): ApiGetClaimCompareResultRequest {
        const paramsData = {
            claim_patent: params.claimPatentId,
            desc_patent: params.descPatentId,
            claim_lang: params.claimLang,
            separate_claims: params.compares.map(compare => {
                const separates = compare.compareFeatures.map(feature => feature.claimVal);
                return { name: compare.name, separates };
            }),
        };
        return paramsData;
    }

    getClaimCompareResult(
        params: FeatureCompareState,
        callback: (streamStatus: StreamStatus, content?: Partial<CompareFeatureData>) => void,
        errorCallback?: (error: { error_code?: number }) => void,
    ): SSEStreamService<ApiGetClaimCompareResultResponse> {
        const paramsData: ApiGetClaimCompareResultRequest = this._createClaimCompareParams(params);
        this._streamCompare = this._getClaimCompareResult.getClaimCompareResult(paramsData)
            .onMessage((data: SSEResponse<ApiGetClaimCompareResultResponse>) => {
                const { status, content } = data.data ?? {};
                if (content) {
                    const compares = content.separate_compares.map(item => ({
                        name: item.name,
                        isIndep: item.indep_claim, // indep_claim: true indicates a main claim
                        compareFeatures: item.separate_compare.map(feature => ({
                            claimVal: feature.claim_feature,
                            id: _.uniqueId(),
                            compareVal: feature.compare_feature,
                            isPublic: feature.is_public,
                            reason: feature.reason,
                        })),
                    }));

                    const resultData = {
                        descLang: content.desc_lang,
                        compares,
                    };
                    callback(status, resultData);
                } else {
                    callback(status);
                }
                if (status === StreamStatus.FINISH) {
                    this._streamCompare?.close();
                }
            })
            .onError((error) => {
                errorCallback?.(error);
            });
        this._streamCompare && this._streamCompare.start();
        return this._streamCompare;
    }
    async getComparePatent(q: string): Promise<GetComparePatentData> {
        const res = await this._searchComparePatent.searchComparePatent({
            q,
            simple: true,
        });
        return {
            patentInfo: res.patent_info,
        };
    }
}
